import React, { ComponentProps, useEffect, useRef, useState } from 'react';
import { CreateJourneyMethodType } from './widget';
import { AutomagicRegularIcon, CloseRegularIcon, ConvertStrongIcon, CopyStrongIcon } from 'src/monet/icons';
import classNames from 'classnames';
import { CreateJourneyQuickStart, QuickStartJourneyStatusParams, QuickStartValidationSchema } from './quickstart';
import { JourneyForm, JourneyFormik } from 'src/common/JourneyFormik';
import { FormikProps } from 'formik';
import { NeueJourneyButton } from 'src/editor/content-creation/components/neue-button/journey';
import {
  CreateJourneyImportAndConvert,
  ImportAndConvertValidationSchema,
  ImportingJourneyStatusParams,
} from './import-and-convert';
import { NeueLoadingSpinner } from 'src/editor/content-creation/components/loading/spinner';
import { NeueTooltip } from 'src/editor/content-creation/components/tooltip';
import { CreateJourneyDuplicate, DuplicateValidationSchema } from './duplicate';
import { Nullable } from 'src/types/nullable.type';
import { CreateJourneyTemplates } from './templates';
import { FloatingDelayGroup } from '@floating-ui/react';
import { CreateJourneyStatus } from './create-journey-status';
import { useHistory, useLocation } from 'react-router-dom';
import { AssistantValidationSchema, CreateJourneyAIAssistant } from './ai-assistant';

export type CreateJourneyTabType = Partial<CreateJourneyMethodType | 'quickstart' | 'assistant'>;

interface TabInterface {
  type: CreateJourneyTabType;
  label: string;
  Icon: (props: ComponentProps<'svg'>) => JSX.Element;
}

interface GenError {
  message: string;
  retry: boolean;
  unhandled?: boolean;
}

const NotAvailables = [
  {
    label: 'Sales followup from Gong',
  },
  {
    label: 'Marketing one pager',
  },
  {
    label: 'Fundraise',
  },
  {
    label: 'Sales deck',
  },
];

const Tabs: TabInterface[] = [
  {
    type: 'assistant',
    label: 'AI Assistant',
    Icon: (props) => (
      <svg className='w-5 h-5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M12 2a10 10 0 100 20 10 10 0 000-20zm1 14.93V17h-2v-.07a8 8 0 01-3.5-1.75l1.42-1.42A6 6 0 0011 15v-2H9v-2h2V9a2 2 0 012-2h2v2h-2v2h2l-1.07 1.07A8 8 0 0113 16.93z' fill='currentColor'/>
      </svg>
    ),
  },
  // {
  //   type: 'automagic',
  //   label: 'Quickstart',
  //   Icon: () => (
  //     <svg className='w-5 h-5' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
  //       <path
  //         d='M8.75 11.25H4.75L11.25 2.75V8.75H15.25L8.75 17.25V11.25Z'
  //         stroke='currentColor'
  //         strokeWidth='1.5'
  //         strokeLinecap='round'
  //         strokeLinejoin='round'
  //       />
  //     </svg>
  //   ),
  // },
  // {
  //   type: 'import',
  //   label: 'Convert',
  //   Icon: ConvertStrongIcon,
  // },
  {
    type: 'duplicate',
    label: 'Duplicate',
    Icon: CopyStrongIcon,
  },
];

export const CreateJourneyPanelContent = ({
  type,
  onClose,
  onSubmit,
}: {
  type: CreateJourneyMethodType;
  onClose: () => void;
  onSubmit: (tab: CreateJourneyTabType) => (formikProps: FormikProps<Record<string, any>>) => void;
}) => {
  const history = useHistory();
  const location = useLocation();

  // Derive the active tab from the URL query parameter 'create' or fallback to the prop 'type'
  const searchParams = new URLSearchParams(location.search);
  const activeTab: CreateJourneyTabType = (searchParams.get('create') as CreateJourneyTabType) || type || null;

  const infoRef = useRef('');

  const renderTabContent = (props: FormikProps<Record<string, any>>) => {
    if (activeTab === 'automagic') {
      return (
        <CreateJourneyQuickStart
          onChange={props.setFieldValue}
          onSubmit={(values) => onSubmit(activeTab)({ ...props, values })}
        />
      );
    }

    if (activeTab === 'import') {
      return (
        <CreateJourneyImportAndConvert
          onChange={props.setFieldValue}
          onSubmit={(values) => onSubmit(activeTab)({ ...props, values })}
        />
      );
    }

    if (activeTab === 'duplicate') {
      return (
        <CreateJourneyDuplicate
          onChange={props.setFieldValue}
          onSubmit={(values) => onSubmit(activeTab)({ ...props, values })}
        />
      );
    }

    if (activeTab === 'assistant') {
      return (
        <CreateJourneyAIAssistant
          onChange={props.setFieldValue}
          onSubmit={(values: Record<string, any>) => onSubmit(activeTab)({ ...props, values })}
        />
      );
    }

    return null;
  };

  const getFormikParams = () => {
    if (activeTab === 'automagic') {
      return {
        initialValues: {
          aiTopic: '',
        },
        validationSchema: QuickStartValidationSchema,
        onSubmit: (values: Record<string, any>, formikProps: FormikProps<Record<string, any>>) =>
          onSubmit(activeTab)({ ...formikProps, values }),
      };
    }

    if (activeTab === 'import') {
      return {
        initialValues: {
          file: null,
          aiTopic: '',
        },
        validationSchema: ImportAndConvertValidationSchema,
        onSubmit: (values: Record<string, any>, formikProps: FormikProps<Record<string, any>>) => {
          formikProps.setFieldValue('error', null);
          return onSubmit(activeTab)({ ...formikProps, values });
        },
      };
    }

    if (activeTab === 'duplicate') {
      return {
        initialValues: {
          recipientDomain: '',
          journeyToDuplicate: null,
          recipientLogoUrl: '',
          recipientName: '',
          name: '',
        },
        onSubmit: (values: Record<string, any>, formikProps: FormikProps<Record<string, any>>) => {
          formikProps.setFieldValue('error', null);
          return onSubmit(activeTab)({ ...formikProps, values });
        },
      };
    }

    if (activeTab === 'assistant') {
      return {
        initialValues: {
          journeyOption: 'Sales Call/Demo Follow-up',
          resources: [],
          articleUrl: '',
          assistantComplete: false
        },
        validationSchema: AssistantValidationSchema,
        onSubmit: (values: Record<string, any>, formikProps: FormikProps<Record<string, any>>) =>
          onSubmit(activeTab)({ ...formikProps, values }),
      };
    }

    return {
      initialValues: {
        aiTopic: '',
      },
      onSubmit: (values: Record<string, any>, formikProps: FormikProps<Record<string, any>>) => {
        formikProps.setFieldValue('error', null);
        return onSubmit(activeTab)({ ...formikProps, values });
      },
    };
  };

  const renderSubmitButtonContent = (error: Nullable<GenError>) => {
    let label = 'Start your journey';
    if (error && error.retry && !error.unhandled) {
      label = 'Retry';
    } else if (activeTab === 'duplicate') {
      label = 'Restart your journey';
    }
    else if (activeTab === 'assistant') {
      label = 'Create your journey';
    }
    return (
      <>
        <span className='text-neue-journey-medium-strong text-inherit'>{label}</span>
        {(!error || error.unhandled) && <AutomagicRegularIcon className='w-5 h-5' />}
      </>
    );
  };

  if (type === 'template') {
    return (
      <>
        <h1 className='text-[60px] font-bold leading-[1.2]'>Templates</h1>
        <CreateJourneyTemplates type='template' onClose={onClose} />
      </>
    );
  }

  if (type === 'organizationTemplate') {
    return (
      <>
        <h1 className='text-[60px] font-bold leading-[1.2]'>Organization Templates</h1>
        <CreateJourneyTemplates type='organization-template' onClose={onClose} />
      </>
    );
  }

  const renderCreationStatus = (info: Nullable<{ message: string }>) => {
    if (activeTab === 'assistant') {
      return <span className="text-neue-journey-small text-inherit">Creating Journey. Please wait a few minutes</span>;
    }
    if (info) {
      return <span className='text-neue-journey-small text-inherit'>{info.message}</span>;
    }
    if (activeTab === 'automagic') {
      return <CreateJourneyStatus {...QuickStartJourneyStatusParams} />;
    }
    if (activeTab === 'import') {
      return <CreateJourneyStatus {...ImportingJourneyStatusParams} />;
    }
    return null;
  };

  const getFormValidation = (values: Record<string, any>) => {
    if (activeTab === 'automagic') {
      return QuickStartValidationSchema.isValidSync(values);
    }
    if (activeTab === 'import') {
      return values.aiTopic.trim() === '' ? !!values.file : true;
    }

    if (activeTab === 'duplicate') {
      return DuplicateValidationSchema.isValidSync(values);
    }
    if (activeTab === 'assistant') {
      return values.assistantComplete === true;
    }

    return false;
  };

  return (
    <>
      <h1 className='text-[60px] font-bold leading-[1.2]'>{activeTab === 'assistant' ? 'AI Assistant' : 'Automagic'}</h1>
      <JourneyFormik {...getFormikParams()}>
        {(props: FormikProps<Record<string, any>>) => {
          const error = props.values?.error || null;
          const info = props.values?.info || null;
          const disablePanel = props.isSubmitting || (error !== null && !error.reset);

          const isFormValid = getFormValidation(props.values);

          if (!infoRef.current && info) {
            infoRef.current = info.message;
            setTimeout(() => {
              props.setFieldValue('info', undefined);
            }, 3000);
          }

          return (
            <>
              <div className='absolute top-0 right-0'>
                <CloseRegularIcon
                  className={classNames('w-5 h-5 m-6 hover:opacity-80 cursor-pointer text-inherit transition-colors', {
                    'opacity-50 pointer-events-none': props.isSubmitting,
                  })}
                  onClick={() => {
                    props.setFieldValue('error', null);
                    onClose();
                  }}
                />
              </div>
              <div className='flex flex-col gap-20 pb-12'>
                <div className='flex gap-4 shrink-0'>
                  <div
                    className={classNames('flex gap-2 shrink-0 journey-theme-dark', {
                      'pointer-events-none': props.isSubmitting,
                    })}
                  >
                    {Tabs.map(({ type, label, Icon }) => (
                      <button
                        key={label}
                        className={classNames('flex flex-row gap-2.5 shrink-0 transition-colors cursor-pointer select-none rounded-2xl px-4 py-[13px] text-neue-journey-medium-strong', {
                          'bg-neue-journey-bg-90 hover:bg-neue-journey-fg-20 text-neue-journey-fg': activeTab !== type,
                          'bg-neue-journey-fg text-neue-journey-bg': activeTab === type,
                        })}
                        onClick={() => {
                          props.setFieldValue('error', null);
                          history.push(`${window.location.pathname}?create=${type}`);
                        }}
                      >
                        <Icon className='w-5 h-5 text-inherit' />
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className='w-px h-8 mx-1 my-2 bg-neue-journey-fg-10'>&nbsp;</div>
                  {/* <FloatingDelayGroup delay={{ open: 10 }}>
                    <NeueTooltip
                      offset={16}
                      placement='top'
                      containerClassName='relative flex gap-2 shrink-0'
                      tooltipContent={
                        <div className='w-[307px] px-2 py-1 rounded bg-neue-journey-bg-90'>
                          Automagic Recipes are coming soon - A few questions that will lead to creating something truly magical
                        </div>
                      }
                    >
                      <div className='absolute inset-0' />
                      {NotAvailables.map(({ label }) => (
                        <button
                          key={label}
                          className={classNames('flex flex-row gap-2.5 shrink-0 transition-colors cursor-pointer select-none rounded-2xl px-4 py-[13px] text-neue-journey-medium-strong bg-neue-journey-bg-90 text-neue-journey-fg pointer-events-none opacity-50')}
                        >
                          {label}
                        </button>
                      ))}
                    </NeueTooltip>
                  </FloatingDelayGroup> */}
                </div>
                <JourneyForm>
                  <div className={classNames('flex flex-col gap-6 shrink-0 text-neue-journey-fg', {
                    'max-w-[676px]': activeTab === 'import',
                    'max-w-[500px]': activeTab && ['automagic', 'duplicate', 'assistant'].includes(activeTab),
                  })}>
                    <div className={classNames('flex flex-col gap-6 shrink-0 text-neue-journey-fg transition-opacity', {
                      'opacity-50 pointer-events-none': disablePanel && !error?.retry,
                      'opacity-100': !disablePanel || error?.reset,
                    })}>
                      {renderTabContent(props)}
                      {activeTab === 'assistant' && (
                        <input type="hidden" name="assistantComplete" value={props.values.assistantComplete ? 'true' : 'false'} />
                      )}
                    </div>
                    <div className='flex gap-6 shrink-0 items-center text-neue-journey-fg'>
                      <NeueJourneyButton
                        type='submit'
                        className={classNames('min-w-[176px] py-[9px] px-4 h-10 text-inherit', {
                          'pointer-events-none': disablePanel && !error?.retry,
                        })}
                        disabled={!isFormValid}
                        containerClassName='flex items-center gap-2'
                        tabIndex={0}
                      >
                        {props.isSubmitting ? (
                          <NeueLoadingSpinner theme='journey' size='16' />
                        ) : (
                          renderSubmitButtonContent(error)
                        )}
                      </NeueJourneyButton>

                      {props.isSubmitting ? (
                        <>{renderCreationStatus(info)}</>
                      ) : (
                        <>{error && <span className='text-neue-journey-small text-inherit'>{error.message}</span>}</>
                      )}
                    </div>
                  </div>
                </JourneyForm>
              </div>
            </>
          );
        }}
      </JourneyFormik>
    </>
  );
};
